import { useIsMobile } from '@shared/hooks/useMobile';
import React from 'react';
import { Checkbox } from '../../../../shared/components/ui';
import { usePendingOrders, useProductSelection } from '../../hooks';
import { AccordionSection, FiltersSection, FloatingActions, GroupingsSection } from '../interaction';
import { PreparationModal } from '../modals';
import { PendingOrdersBreadcrumb } from '../display';
import { PendingOrdersGridMemo } from './PendingOrdersGrid';
import { PendingOrdersStates } from './PendingOrdersStates';

const Main: React.FC = () => {
    const isMobile = useIsMobile();

    // Hook para datos y procesamiento
    const {
        pendingOrders,
        isLoading,
        isFetching,
        error,
        totalRaw,
        filters,
        updateFilters,
        uniqueValues,
        groupingFields,
        toggleGrouping,
        clearGroupings,
        groupingStats,
        getGroupingDescription,
    } = usePendingOrders();

    // 🎯 Hook para gestión de selección
    const {
        selectedProducts,
        areAllProductsSelected,
        areSomeProductsSelected,
        toggleProductSelection,
        toggleSelectAll,
        openPreparationModal,
        deselectAllProducts,
        preparationModalRef
    } = useProductSelection(pendingOrders);

    // Mostrar estados de carga y error
    const stateComponent = (
        <PendingOrdersStates isLoading={isLoading} error={error} />
    );

    if (isLoading || error) {
        return stateComponent;
    }

    // 🎯 Layout para Mobile
    if (isMobile) {
        return (
            <>
                <PendingOrdersBreadcrumb />
                <div className="flex flex-col h-full p-4 gap-4">
                {/* Acordeón de Filtros - Ocupa 100% del ancho */}
                <AccordionSection title="Filtros" defaultOpen={false}>
                    <FiltersSection
                        filters={filters}
                        updateFilters={updateFilters}
                        uniqueValues={uniqueValues}
                        isInsideAccordion={true}
                    />
                </AccordionSection>

                {/* Acordeón de Agrupaciones - Ocupa 100% del ancho */}
                <AccordionSection title="Agrupar" defaultOpen={false}>
                    <GroupingsSection
                        groupingFields={groupingFields}
                        toggleGrouping={toggleGrouping}
                        clearGroupings={clearGroupings}
                        groupingStats={groupingStats}
                        getGroupingDescription={getGroupingDescription}
                        isInsideAccordion={true}
                    />
                </AccordionSection>

                {/* Lista de Productos - Ocupa el resto del espacio */}
                <div className="flex-1 flex flex-col min-h-0">
                    <div className="sticky top-0 bg-background rounded-t-lg z-10 border border-border border-b-0 p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <Checkbox
                                    checked={areAllProductsSelected}
                                    onCheckedChange={toggleSelectAll}
                                />
                                <span className="text-sm font-medium ml-2">
                                    {areSomeProductsSelected ?
                                        `${selectedProducts.size} de ${pendingOrders.length}` :
                                        'Seleccionar todos'
                                    }
                                </span>
                            </div>

                            {/* Indicador de actualización */}
                            {isFetching && !isLoading && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto border rounded-lg rounded-t-none border-border min-h-[500px]">
                        <PendingOrdersGridMemo
                            pendingOrders={pendingOrders}
                            totalRaw={totalRaw}
                            groupingFields={groupingFields}
                            selectedProducts={selectedProducts}
                            toggleProductSelection={toggleProductSelection}
                        />
                    </div>
                </div>

                {/* Acciones Flotantes - Solo se muestran cuando hay productos seleccionados */}
                <FloatingActions
                    selectedCount={selectedProducts.size}
                    totalCount={pendingOrders.length}
                    onSendToPreparation={openPreparationModal}
                    onClearSelection={deselectAllProducts}
                />

                {/* Modal de Preparación */}
                <PreparationModal
                    ref={preparationModalRef}
                    onPreparationSuccess={deselectAllProducts}
                />
                </div>
            </>
        );
    }

    // 🎯 Layout para Desktop (original)
    return (
        <>
            <PendingOrdersBreadcrumb />
            <div className="flex h-full p-6 gap-4">
            {/* Columna Izquierda - Filtros y Agrupaciones */}
            <div className="w-80 flex flex-col gap-4">
                {/* Bloque de Filtros */}
                <FiltersSection
                    filters={filters}
                    updateFilters={updateFilters}
                    uniqueValues={uniqueValues}
                />

                {/* Bloque de Agrupaciones */}
                <GroupingsSection
                    groupingFields={groupingFields}
                    toggleGrouping={toggleGrouping}
                    clearGroupings={clearGroupings}
                    groupingStats={groupingStats}
                    getGroupingDescription={getGroupingDescription}
                />
            </div>

            {/* Columna Derecha - Lista de Productos */}
            <div className="flex-1 flex flex-col">
                <div className="sticky top-0 bg-background rounded-t-lg z-10 border border-border border-b-0 p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <Checkbox
                                checked={areAllProductsSelected}
                                onCheckedChange={toggleSelectAll}
                            />
                            <span className="text-sm font-medium ml-2">
                                {areSomeProductsSelected ?
                                    `${selectedProducts.size} de ${pendingOrders.length} seleccionados` :
                                    'Seleccionar todos'
                                }
                            </span>
                        </div>

                        {/* Indicador de actualización */}
                        {isFetching && !isLoading && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                                <span>Actualizando...</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="overflow-auto border rounded-lg rounded-t-none border-border">
                    <PendingOrdersGridMemo
                        pendingOrders={pendingOrders}
                        totalRaw={totalRaw}
                        groupingFields={groupingFields}
                        selectedProducts={selectedProducts}
                        toggleProductSelection={toggleProductSelection}
                    />
                </div>
            </div>

            {/* Acciones Flotantes - Solo se muestran cuando hay productos seleccionados */}
            <FloatingActions
                selectedCount={selectedProducts.size}
                totalCount={pendingOrders.length}
                onSendToPreparation={openPreparationModal}
                onClearSelection={deselectAllProducts}
            />

            {/* Modal de Preparación */}
            <PreparationModal
                ref={preparationModalRef}
                onPreparationSuccess={deselectAllProducts}
            />
            </div>
        </>
    );
};

export { Main };
